import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { Input, PolarQuestion, AlertNotice, AlertMessage } from 'shared/components';
import * as tcRenewalAction from 'features/tc-renewal/duck';
import { StorageRadioList, AddPerson } from 'features/tc-renewal';
import { secureStaffErrorStrings, secureStaffRules } from 'shared/validations';
import { ValidationService } from 'shared/services';
import { getMappedContactTypes } from 'features/tc-renewal/selectors';
import { CONTACT_TYPES } from 'shared/constants';
import * as helper from './helper';
import './TCSecureStorageContainer.scss';

const secureStorageDTO = {
  storageContainer: '',
  storageLocation: '',
  storageContainerOther: '',
  storageLocationOther: '',
  storageOtherUse: '',
};

class TCSecureStorageContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tcCycle: {
        ...secureStorageDTO,
        ...props.tcCycle,
      },
      studentsQuestion: {
        ...helper.studentsQuestion,
        initial: props.tcCycle.storageStudentAccess ? 'Yes' : 'No',
        key: 'storageStudentAccess',
      },
      showSuccessMessage: false,
      successMessage: '',
      errors: {},
      isDirty: false,
    };

    this.handleContinue = this.handleContinue.bind(this);
    this.updateContactsList = this.updateContactsList.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onQuestionChange = this.onQuestionChange.bind(this);
    this.removeSuccessMessage = this.removeSuccessMessage.bind(this);
    this.getContacts = this.getContacts.bind(this);
    this.checkIsDirty = this.checkIsDirty.bind(this);
  }

  componentDidMount() {
    const { actions, mappedContactTypes } = this.props;

    if (isEmpty(mappedContactTypes)) {
      actions.getContactTypes().then(() => this.getContacts());
    } else {
      this.getContacts();
    }
  }

  getContacts() {
    const {
      tcCycle,
      actions,
      alternateContacts,
      secureStorageContacts,
      secureMaterialsContacts,
      mappedContactTypes,
    } = this.props;

    if (!alternateContacts.length) {
      actions.getTcCycleContacts(tcCycle.id, mappedContactTypes[CONTACT_TYPES.alternateContact]);
    }
    if (!secureStorageContacts.length) {
      actions.getTcCycleContacts(
        tcCycle.id,
        mappedContactTypes[CONTACT_TYPES.securityStorageContact]
      );
    }
    if (!secureMaterialsContacts.length) {
      actions.getTcCycleContacts(
        tcCycle.id,
        mappedContactTypes[CONTACT_TYPES.securityMaterialsTransferContact]
      );
    }
  }

  onChange(e) {
    const { name, value } = e.target;
    const { errors } = this.state;
    if (name === 'storageLocation' || name === 'storageContainer') {
      delete errors[`${name}Other`];
    }
    delete errors[name];

    this.setState((prevState) => ({
      tcCycle: {
        ...prevState.tcCycle,
        [name]: value,
      },
      errors: {
        ...errors,
      },
      isDirty: true,
    }));
  }

  onQuestionChange(question, option) {
    const tcCycle = { ...this.state.tcCycle };
    tcCycle[question.key] = option === 'Yes' ? !!option : false;

    this.setState({
      tcCycle,
      isDirty: true,
    });
  }

  async updateContactsList(currentPerson, listOfPersons, remove) {
    const { tcCycle } = this.props;

    if (remove) {
      return await this.props.actions.removeTcCycleContact(currentPerson.id).then(() => {
        if (isEmpty(this.props.errorRemovingContact)) {
          this.props.actions.getTcCycleContactsAction({
            data: listOfPersons,
            payload: { typeId: currentPerson.contactType.id },
          });
          this.setState({
            showSuccessMessage: true,
            successMessage: 'testCenter.storage.successRemoveMessage',
          });
        }
      });
    }
    if (!currentPerson.id) {
      currentPerson.testCenterCycle = { id: tcCycle.id };

      return await this.props.actions.setTcCycleContact(currentPerson).then(() => {
        if (isEmpty(this.props.errorSetContact)) {
          this.props.actions.getTcCycleContacts(tcCycle.id, currentPerson.contactType.id);
          this.setState({
            showSuccessMessage: true,
            successMessage: 'testCenter.storage.successAddMessage',
          });
          delete this.state.errors.secureStorageContacts;
          delete this.state.errors.secureMaterialsContacts;
        }
      });
    }

    return await this.props.actions.setTcCycleContact(currentPerson).then(() => {
      if (isEmpty(this.props.errorSetContact)) {
        this.props.actions.getTcCycleContactsAction({
          data: listOfPersons,
          payload: { typeId: currentPerson.contactType.id },
        });
        this.setState({
          showSuccessMessage: true,
          successMessage: 'testCenter.storage.successEditMessage',
        });
      }
    });
  }

  removeSuccessMessage() {
    this.setState({
      showSuccessMessage: false,
      successMessage: '',
    });
  }

  handleErrors() {
    const { tcCycle } = this.state;

    const errors = ValidationService.validate(tcCycle, secureStaffRules, secureStaffErrorStrings);

    if (isUndefined(tcCycle.storageStudentAccess)) {
      errors.storageStudentAccess = ['errors.selectOption'];
    }
    if (tcCycle.storageLocation === 'other' && !tcCycle.storageLocationOther) {
      errors.storageLocationOther = ['errors.fieldMandatory'];
    }
    if (tcCycle.storageContainer === 'other' && !tcCycle.storageContainerOther) {
      errors.storageContainerOther = ['errors.fieldMandatory'];
    }

    if (!isEmpty(errors)) {
      this.setState({
        errors,
      });
      return true;
    }

    return false;
  }

  async handleContinue() {
    const { tcCycle } = this.state;
    tcCycle.storageLocationOther =
      tcCycle.storageLocation === 'other' ? tcCycle.storageLocationOther : '';
    tcCycle.storageContainerOther =
      tcCycle.storageContainer === 'other' ? tcCycle.storageContainerOther : '';

    if (this.handleErrors()) {
      return false;
    }

    const cycleUpdated = await this.props.actions.updateTCCycle(tcCycle);
    if (cycleUpdated && cycleUpdated.message) {
      return { error: true };
    }

    return !!cycleUpdated;
  }

  checkIsDirty() {
    return this.state.isDirty;
  }

  render() {
    const {
      secureStorageContacts,
      secureMaterialsContacts,
      alternateContacts,
      mappedContactTypes,
    } = this.props;
    const { tcCycle, studentsQuestion, showSuccessMessage, successMessage, errors } = this.state;

    if (!alternateContacts.length) {
      return null;
    }

    return (
      <section className="secure-storage-container">
        <h4>{this.props.t('testCenter.storage.title')}</h4>
        <p className="pb-3">{this.props.t('testCenter.storage.subtitle')}</p>
        {!isEmpty(errors) && (
          <AlertNotice
            type="error"
            message={this.props.t('testCenter.storage.mandatoryFieldsWarning')}
          />
        )}
        <div className="secure-storage-content border-gray">
          <StorageRadioList
            className="border-bottom-gray"
            title={this.props.t('testCenter.storage.locationLabel')}
            checkedValue={tcCycle.storageLocation}
            inputValue={tcCycle.storageLocationOther}
            radioOptions={helper.storageLocationOptions}
            updateTc={this.onChange}
            errorRadio={errors.storageLocation}
            errorOther={errors.storageLocationOther}
          />
          <div className="other-use-input border-bottom-gray pt-4">
            <Input
              type="text"
              name="storageOtherUse"
              id="storageOtherUse"
              value={tcCycle.storageOtherUse}
              label="testCenter.storage.otherUseLabel"
              onChange={this.onChange}
              errors={errors.storageOtherUse}
              required
            />
          </div>
          <StorageRadioList
            className="border-bottom-gray pt-4"
            title={this.props.t('testCenter.storage.typeLabel')}
            checkedValue={tcCycle.storageContainer}
            inputValue={tcCycle.storageContainerOther}
            radioOptions={helper.storageContainerOptions}
            updateTc={this.onChange}
            errorRadio={errors.storageContainer}
            errorOther={errors.storageContainerOther}
          />
          <AddPerson
            className="border-bottom-gray pt-4 pb-4"
            listOfPersons={secureStorageContacts}
            contactTypeId={mappedContactTypes[CONTACT_TYPES.securityStorageContact]}
            alternateContact={alternateContacts[0]}
            updateContactsList={this.updateContactsList}
            labels={helper.storageAccessPersonsLabels}
            error={errors.secureStorageContacts}
          />
          <PolarQuestion
            className="pt-4"
            question={studentsQuestion}
            onChange={this.onQuestionChange}
            errors={errors.storageStudentAccess}
          />
        </div>
        <div className="border-gray">
          <AddPerson
            listOfPersons={secureMaterialsContacts}
            contactTypeId={mappedContactTypes[CONTACT_TYPES.securityMaterialsTransferContact]}
            alternateContact={alternateContacts[0]}
            updateContactsList={this.updateContactsList}
            labels={helper.materialTransferPersonsLabels}
            error={errors.secureMaterialsContacts}
          />
        </div>
        <AlertMessage
          show={showSuccessMessage}
          removeMessage={this.removeSuccessMessage}
          message={successMessage}
        />
      </section>
    );
  }
}

function mapStateToProps(state) {
  return {
    tcCycle: state.tcRenewal.tcCycle,
    currentUser: state.users.currentUser,
    mappedContactTypes: getMappedContactTypes(state),
    alternateContacts: state.tcRenewal.alternateContacts,
    secureStorageContacts: state.tcRenewal.secureStorageContacts,
    secureMaterialsContacts: state.tcRenewal.secureMaterialsTransfContacts,
    loadingContacts: state.tcRenewal.loadingContacts,
    errorSetContact: state.tcRenewal.errorSetContact,
    errorRemovingContact: state.tcRenewal.errorRemovingContact,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...tcRenewalAction }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  withTranslation(null, { withRef: true })(TCSecureStorageContainer)
);

TCSecureStorageContainer.propTypes = {
  tcCycle: PropTypes.shape({}).isRequired,
  currentUser: PropTypes.shape().isRequired,
  mappedContactTypes: PropTypes.shape({}),
  alternateContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  secureStorageContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  secureMaterialsContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  actions: PropTypes.shape({
    getTcCycleContacts: PropTypes.func,
    setTcCycleContact: PropTypes.func,
    removeTcCycleContact: PropTypes.func,
    getTcCycleContactsAction: PropTypes.func,
    updateTCCycle: PropTypes.func,
  }).isRequired,
  t: PropTypes.func.isRequired,
};
